import { API_ROUTES } from "../utils/constants"
import axios from "axios"

// JWT Token Management
class AuthService {
    constructor() {
        this.tokenKey = "jwt_token"
        this.refreshTokenKey = "jwt_refresh_token"
        this.userKey = "user_data"
        this.tokenExpiryKey = "token_expiry"
    }

    // Store JWT token with expiration
    setToken(token, expiresIn = null) {
        try {
            localStorage.setItem(this.tokenKey, token)

            // Calculate expiration time (default 1 hour if not provided)
            const expiryTime = expiresIn ? Date.now() + expiresIn * 1000 : Date.now() + 60 * 60 * 1000 // 1 hour default

            localStorage.setItem(this.tokenExpiryKey, expiryTime.toString())

            // Set up automatic token refresh
            this.setupTokenRefresh(expiryTime)

            return true
        } catch (error) {
            console.error("Error storing token:", error)
            return false
        }
    }

    // Get stored JWT token
    getToken() {
        try {
            return localStorage.getItem(this.tokenKey)
        } catch (error) {
            console.error("Error getting token:", error)
            return null
        }
    }

    // Check if token is valid and not expired
    isTokenValid() {
        try {
            const token = this.getToken()
            if (!token) return false

            const expiryTime = localStorage.getItem(this.tokenExpiryKey)
            if (!expiryTime) return false

            // Check if token is expired (with 5 minute buffer)
            const currentTime = Date.now()
            const expiryTimestamp = parseInt(expiryTime)
            const bufferTime = 5 * 60 * 1000 // 5 minutes

            return currentTime < expiryTimestamp - bufferTime
        } catch (error) {
            console.error("Error checking token validity:", error)
            return false
        }
    }

    // Store refresh token
    setRefreshToken(refreshToken) {
        try {
            localStorage.setItem(this.refreshTokenKey, refreshToken)
            return true
        } catch (error) {
            console.error("Error storing refresh token:", error)
            return false
        }
    }

    // Get refresh token
    getRefreshToken() {
        try {
            return localStorage.getItem(this.refreshTokenKey)
        } catch (error) {
            console.error("Error getting refresh token:", error)
            return null
        }
    }

    // Store user data
    setUserData(userData) {
        try {
            localStorage.setItem(this.userKey, JSON.stringify(userData))
            return true
        } catch (error) {
            console.error("Error storing user data:", error)
            return false
        }
    }

    // Get user data
    getUserData() {
        try {
            const userData = localStorage.getItem(this.userKey)
            return userData ? JSON.parse(userData) : null
        } catch (error) {
            console.error("Error getting user data:", error)
            return null
        }
    }

    // Setup automatic token refresh
    setupTokenRefresh(expiryTime) {
        // Clear any existing refresh timer
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer)
        }

        // Calculate time until refresh (refresh 5 minutes before expiry)
        const refreshTime = expiryTime - Date.now() - 5 * 60 * 1000 // 5 minutes before expiry

        if (refreshTime > 0) {
            this.refreshTimer = setTimeout(() => {
                this.refreshToken()
            }, refreshTime)
        }
    }

    // Refresh JWT token
    async refreshToken() {
        try {
            const refreshToken = this.getRefreshToken()
            const token = this.getToken()
            if (!refreshToken || !token) {
                throw new Error("No refresh token available")
            }

            const response = await axios.post(
                API_ROUTES.USER.REFRESH_TOKEN || `${API_ROUTES.USER.AUTHENTICATE}/refresh`,
                {
                    refreshToken: refreshToken,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token,
                    },
                },
            )

            if (response.data.token) {
                this.setToken(response.data.token, response.data.expires_in)
                if (response.data.refresh_token) {
                    this.setRefreshToken(response.data.refresh_token)
                }
                return response.data.token
            } else {
                throw new Error("No token received in refresh response")
            }
        } catch (error) {
            console.error("Token refresh failed:", error)
            // If refresh fails, logout the user
            this.logout()
            throw error
        }
    }

    // Decode JWT token (without verification - for client-side use only)
    decodeToken(token) {
        try {
            const base64Url = token.split(".")[1]
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map(function (c) {
                        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
                    })
                    .join(""),
            )
            return JSON.parse(jsonPayload)
        } catch (error) {
            console.error("Error decoding token:", error)
            return null
        }
    }

    // Get token payload
    getTokenPayload() {
        const token = this.getToken()
        return token ? this.decodeToken(token) : null
    }

    // Check if user has specific role/permission
    hasRole(role) {
        const payload = this.getTokenPayload()
        if (!payload) return false

        // Check for roles in token payload (adjust based on your JWT structure)
        if (payload.roles && Array.isArray(payload.roles)) {
            return payload.roles.includes(role)
        }

        if (payload.role) {
            return payload.role === role
        }

        return false
    }

    // Check if user has specific permission
    hasPermission(permission) {
        const payload = this.getTokenPayload()
        if (!payload) return false

        // Check for permissions in token payload (adjust based on your JWT structure)
        if (payload.permissions && Array.isArray(payload.permissions)) {
            return payload.permissions.includes(permission)
        }

        return false
    }

    // Logout user
    logout() {
        try {
            // Clear all authentication data
            localStorage.clear()

            

            // Clear any existing refresh timer
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer)
                this.refreshTimer = null
            }

            return true
        } catch (error) {
            console.error("Error during logout:", error)
            return false
        }
    }

    // Initialize authentication service
    init() {
        // Setup axios interceptors

        // Check if we have a valid token and setup refresh if needed
        if (this.isTokenValid()) {
            const expiryTime = localStorage.getItem(this.tokenExpiryKey)
            if (expiryTime) {
                this.setupTokenRefresh(parseInt(expiryTime))
            }
        }
    }
}

// Create and export singleton instance
const authService = new AuthService()

// Initialize the service
authService.init()

export default authService

// Export individual functions for backward compatibility
export const {
    setToken,
    getToken,
    isTokenValid,
    setRefreshToken,
    getRefreshToken,
    setUserData,
    getUserData,
    refreshToken,
    decodeToken,
    getTokenPayload,
    hasRole,
    hasPermission,
    logout,
    init,
} = authService
